/**
 * Firebase Configuration and Push Notification Service
 * Handles FCM token registration and notification permissions
 */

import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// VAPID key for web push from environment variable
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

// Initialize Firebase (singleton)
let firebaseApp = null;
let messagingInstance = null;

export function getFirebaseApp() {
  if (!firebaseApp) {
    firebaseApp = getApps()[0] || initializeApp(firebaseConfig);
  }
  return firebaseApp;
}

export async function getFirebaseMessaging() {
  if (messagingInstance) {
    return messagingInstance;
  }
  
  // Check if messaging is supported
  const supported = await isSupported();
  if (!supported) {
    console.warn('Firebase Messaging is not supported in this browser');
    return null;
  }
  
  const app = getFirebaseApp();
  messagingInstance = getMessaging(app);
  return messagingInstance;
}

/**
 * Request notification permission and get FCM token
 * @returns {Promise<string|null>} FCM token or null if denied/unsupported
 */
export async function requestNotificationPermission() {
  try {
    // Check if notifications are supported
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return null;
    }
    
    // Check current permission status
    let permission = Notification.permission;
    
    // Request permission if not already granted
    if (permission === 'default') {
      permission = await Notification.requestPermission();
    }
    
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return null;
    }
    
    // Get Firebase messaging instance
    const messaging = await getFirebaseMessaging();
    if (!messaging) {
      return null;
    }
    
    // Register service worker
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.log('Service Worker registered:', registration);
    
    // Get FCM token
    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });
    
    console.log('FCM Token obtained:', token?.substring(0, 20) + '...');
    return token;
    
  } catch (error) {
    console.error('Error getting notification permission:', error);
    return null;
  }
}

/**
 * Listen for foreground messages
 * @param {function} callback - Function to call when message received
 * @returns {function|null} Unsubscribe function or null
 */
export async function onForegroundMessage(callback) {
  try {
    const messaging = await getFirebaseMessaging();
    if (!messaging) {
      return null;
    }
    
    return onMessage(messaging, (payload) => {
      console.log('Foreground message received:', payload);
      callback(payload);
    });
    
  } catch (error) {
    console.error('Error setting up foreground message listener:', error);
    return null;
  }
}

/**
 * Check if push notifications are supported and enabled
 * @returns {Promise<object>} Status object with supported, permission, and token fields
 */
export async function getNotificationStatus() {
  const status = {
    supported: false,
    permission: 'default',
    hasToken: false,
    browserSupport: {
      notifications: 'Notification' in window,
      serviceWorker: 'serviceWorker' in navigator,
      pushManager: 'PushManager' in window
    }
  };
  
  // Check browser support
  status.supported = status.browserSupport.notifications && 
                     status.browserSupport.serviceWorker && 
                     status.browserSupport.pushManager;
  
  if (!status.supported) {
    return status;
  }
  
  // Check permission
  status.permission = Notification.permission;
  
  // Check if we have a token (from localStorage cache)
  const cachedToken = localStorage.getItem('fcm_token');
  status.hasToken = !!cachedToken;
  
  return status;
}

/**
 * Save FCM token to localStorage for caching
 * @param {string} token - FCM token to save
 */
export function saveFcmToken(token) {
  if (token) {
    localStorage.setItem('fcm_token', token);
  } else {
    localStorage.removeItem('fcm_token');
  }
}

/**
 * Get cached FCM token from localStorage
 * @returns {string|null} Cached FCM token or null
 */
export function getCachedFcmToken() {
  return localStorage.getItem('fcm_token');
}
